/**
 * useDispatchBoard Hook
 *
 * React Query hook for managing dispatch board operations
 *
 * Features:
 * - Fetch board with automatic polling
 * - Initialize board
 * - Add/remove drivers with optimistic updates
 * - Toast notifications for all operations
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { dispatchApi } from '../api/dispatchApi';
import { toast } from 'sonner';

export function useDispatchBoard() {
  const queryClient = useQueryClient();

  // Fetch board
  const {
    data: board,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['dispatch', 'board'],
    queryFn: () => dispatchApi.getBoard(),
    staleTime: 30000, // 30 seconds
    refetchInterval: 60000, // Poll every minute
    retry: 2,
  });

  // Initialize board
  const initializeMutation = useMutation({
    mutationFn: () => dispatchApi.initializeBoard(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dispatch', 'board'] });
      toast.success('Dispatch board initialized');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to initialize board');
    },
  });

  // Add driver
  const addDriverMutation = useMutation({
    mutationFn: (driverName: string) => dispatchApi.addDriver(driverName),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dispatch', 'board'] });
      toast.success('Driver added successfully');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to add driver');
    },
  });

  // Remove driver
  const removeDriverMutation = useMutation({
    mutationFn: (driverName: string) => dispatchApi.removeDriver(driverName),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dispatch', 'board'] });
      queryClient.invalidateQueries({ queryKey: ['dispatch', 'assignments'] });
      toast.success('Driver removed successfully');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to remove driver');
    },
  });

  return {
    board,
    isLoading,
    error,
    refetch,
    initializeBoard: initializeMutation.mutate,
    addDriver: addDriverMutation.mutate,
    removeDriver: removeDriverMutation.mutate,
    isInitializing: initializeMutation.isPending,
    isAddingDriver: addDriverMutation.isPending,
    isRemovingDriver: removeDriverMutation.isPending,
  };
}
